import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { AudioContextManager } from "@/lib/audio-utils";

interface AudioContextState {
  isInitialized: boolean;
  isPlaying: boolean;
  audioLevel: number;
  frequencyData: Uint8Array | null;
  audioManager: AudioContextManager | null;
  connectAudioElement: (element: HTMLAudioElement) => (() => void) | undefined;
  resume: () => Promise<void>;
}

const AudioContext = createContext<AudioContextState | null>(null);

export function useAudioContext() {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error("useAudioContext must be used within AudioContextProvider");
  }
  return context;
}

interface AudioContextProviderProps {
  children: React.ReactNode;
}

export function AudioContextProvider({ children }: AudioContextProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [frequencyData, setFrequencyData] = useState<Uint8Array | null>(null);
  const audioManagerRef = useRef<AudioContextManager | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    const initializeAudio = async () => {
      try {
        const manager = AudioContextManager.getInstance();
        await manager.initialize({
          fftSize: 256,
          smoothingTimeConstant: 0.8,
        });
        audioManagerRef.current = manager;
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize audio context:", error);
      }
    };

    initializeAudio();

    return () => {
      if (audioManagerRef.current) {
        audioManagerRef.current.dispose();
      }
    };
  }, []);

  useEffect(() => {
    if (!isInitialized || !audioManagerRef.current) {
      return;
    }

    const updateAudioData = () => {
      const manager = audioManagerRef.current;
      if (!manager) {
        return;
      }

      const data = manager.getFrequencyData();
      if (data) {
        setAudioLevel(data.average / 255);
        setFrequencyData(data.frequencies);
      }

      animationFrameRef.current = requestAnimationFrame(updateAudioData);
    };

    updateAudioData();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isInitialized]);

  const connectAudioElement = useCallback((element: HTMLAudioElement) => {
    if (!audioManagerRef.current) {
      return;
    }

    if (audioElementRef.current === element) {
      return;
    }

    audioElementRef.current = element;

    try {
      audioManagerRef.current.connectAudioElement(element);
    } catch (error) {
      console.error("Failed to connect audio element:", error);
      audioElementRef.current = null;
      return;
    }

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    element.addEventListener("play", handlePlay);
    element.addEventListener("pause", handlePause);
    element.addEventListener("ended", handleEnded);

    return () => {
      element.removeEventListener("play", handlePlay);
      element.removeEventListener("pause", handlePause);
      element.removeEventListener("ended", handleEnded);
      audioElementRef.current = null;
    };
  }, []);

  const resume = useCallback(async () => {
    if (audioManagerRef.current) {
      await audioManagerRef.current.resume();
    }
  }, []);

  const value: AudioContextState = {
    audioLevel,
    audioManager: audioManagerRef.current,
    connectAudioElement,
    frequencyData,
    isInitialized,
    isPlaying,
    resume,
  };

  return (
    <AudioContext.Provider value={value}>{children}</AudioContext.Provider>
  );
}
